import './ShareCard.css';
import type { JeffCall } from '../../../shared/types.ts';
import { JEFF } from '../../config/jeff.ts';
import { formatShareDate } from '../../lib/formatting.ts';

interface ShareCardProps {
  call: JeffCall;
  /** Renders at full 1200×630; otherwise fluid within its container. */
  fixed?: boolean;
}

/**
 * The card people screenshot.
 *
 * Laid out at 1200×630 — OpenGraph proportions — and sized entirely in
 * container-query units, so a phone screenshot and a 2× desktop capture are the
 * same composition at different resolutions. Nothing here depends on an image
 * service; a screenshot of this element *is* the asset.
 */
export function ShareCard({ call, fixed = false }: ShareCardProps) {
  const isLong = call.direction === 'LONG';

  return (
    <figure className={`sharecard sharecard--${call.direction.toLowerCase()}${fixed ? ' sharecard--fixed' : ''}`}>
      <div className="sharecard__grid" aria-hidden="true" />
      <div className="sharecard__wash" aria-hidden="true" />

      <header className="sharecard__head">
        <span className="sharecard__brand">
          <b>{JEFF.appNameParts[0]}</b>
          <i>{JEFF.appNameParts[1]}</i>
        </span>
        <span className="sharecard__id">{call.id}</span>
      </header>

      <div className="sharecard__main">
        {/* A live call names no market, so the verdict carries the card on its
            own. An imported historical call leads with its asset instead. */}
        {call.asset && <p className="sharecard__asset">{call.asset}</p>}
        {call.price && <p className="sharecard__price">{call.price}</p>}

        <p className="sharecard__says">JEFF SAYS:</p>

        <p className="sharecard__direction">
          <span className="sharecard__arrow" aria-hidden="true">
            {isLong ? '▲' : '▼'}
          </span>
          {call.direction}
        </p>

        {call.context ? (
          <p className="sharecard__quote">&ldquo;{call.context}&rdquo;</p>
        ) : (
          <p className="sharecard__quote sharecard__quote--default">&ldquo;The oracle has spoken.&rdquo;</p>
        )}
      </div>

      <footer className="sharecard__foot">
        <span>{formatShareDate(call.createdAt)}</span>
        <span className="sharecard__dot" aria-hidden="true" />
        <span>{call.jeffVersion}</span>
        <span className="sharecard__domain">{JEFF.publicDomain}</span>
      </footer>

      <figcaption className="visually-hidden">
        {JEFF.appName} call {call.id}: Jeff went {call.direction} on {call.asset}.
      </figcaption>
    </figure>
  );
}
